import { Checkbox, Chip, Switch } from "@heroui/react";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { CircleCheck } from "lucide-react";

import EditableTextCell from "@/components/common/EditableTextCell";
import EditableChip from "@/components/common/EditableChip";
import LinkCell from "@/components/common/LinkCell";
import ProgressGauge from "@/components/common/ProgressGauge";
import type { SingleFieldKind } from "@/components/common/SingleFieldEditDialog";
import "@/components/table/columnMeta";
import { formatAmount } from "@/lib/format";

import {
  QUOTATION_STATUS_LABEL,
  type Quotation,
  type QuotationInput,
  type QuotationStatus,
} from "./api";

/**
 * 見積一覧の列定義。**ヘッダとセルの単一ソース**。
 *
 * ngtg_msc の ProjectColumn と同じ考え方で、列のラベル・並び替えキー・幅・
 * セルの描画をここにまとめる。表示列の設定ダイアログもこの定義（meta.label /
 * meta.mandatory）を読むため、列を増やすときはここだけ触ればよい。
 *
 * `meta.sortKey` は API に渡す並び替えキー。未指定の列は並び替え不可。
 *
 * 直せる項目は**クリックで 1 項目だけ編集するセル**（[[EditableTextCell]]）に
 * してある。編集ダイアログの開閉は一覧側が持つ（セルごとに Modal を持たせない）。
 * 案件一覧・取引先一覧と同じ作り。
 *
 * **直せない列もある**。
 *
 * - **見積番号** … 詳細へのリンク。採番は API がするので画面からは変えない
 * - **得意先** … 一覧からは変えさせない。得意先を替えると回答ファイルの様式
 *   （指定業者）まで変わるので、流し見の最中に押し替えられると困る。変えるのは
 *   見積詳細の「見積依頼情報」から
 * - **進捗 / 明細 / 金額 / 回答納期** … 明細から導く値で、見積ヘッダは持たない
 */
const helper = createColumnHelper<Quotation>();

/**
 * 状態ごとの色。受注=緑・失注=赤で、一覧を流し見して結果が分かるようにする。
 * 辞退は失注と区別できるよう別の色にする（見積を出していないため）。
 */
const STATUS_COLOR: Record<
  QuotationStatus,
  "default" | "primary" | "secondary" | "success" | "danger" | "warning"
> = {
  requested: "default",
  draft: "warning",
  submitted: "primary",
  ordered: "success",
  lost: "danger",
  declined: "secondary",
};

/** 今日（ローカルではなく UTC 基準の ISO 日付）。期限切れの判定に使う。 */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 決着済みの見積。期限の色分けは意味が無くなる。 */
function settled(status: QuotationStatus): boolean {
  return ["ordered", "lost", "declined"].includes(status);
}

/**
 * クリックで編集できる項目。編集ダイアログの設定もここに持つ。
 *
 * **見せる値と直す値が違うことがある**（特急は「特急/通常」を見せて真偽値を
 * 直す）ので、ダイアログに渡す値は `value` で別に取る。
 */
export interface QuotationField {
  /** 編集ダイアログの見出しに出す項目名。 */
  label: string;
  kind?: SingleFieldKind;
  required?: boolean;
  /** 編集ダイアログの補足。 */
  description?: string;
  options?: { key: string; label: string }[];
  /** ダイアログの初期値（見せている文字列とは限らない）。 */
  value: (quotation: Quotation) => string | null;
  /** 入力された値を API に送る形にする。 */
  toInput: (value: string | null) => QuotationInput;
}

const FIELDS = {
  client_request_number: {
    label: "客先依頼番号",
    value: (q) => q.client_request_number,
    toInput: (v) => ({ client_request_number: v }),
  },
  subject: {
    label: "件名",
    value: (q) => q.subject,
    toInput: (v) => ({ subject: v }),
  },
  set_count: {
    label: "セット数",
    kind: "number",
    description:
      "何セット作るか。入れるとセット品になります。明細の数量は「1 セットあたり × セット数」の合算です。",
    value: (q) => (q.set_count == null ? null : String(q.set_count)),
    toInput: (v) => ({ set_count: v == null || v === "" ? null : Number(v) }),
  },
  is_urgent: {
    label: "特急",
    kind: "select",
    required: true,
    description: "客先の緊急区分です。状態とは別に立ちます。",
    options: [
      { key: "false", label: "通常" },
      { key: "true", label: "特急" },
    ],
    value: (q) => (q.is_urgent ? "true" : "false"),
    toInput: (v) => ({ is_urgent: v === "true" }),
  },
  status: {
    label: "状態",
    kind: "select",
    required: true,
    description: "失注にする場合は先に失注理由を入力してください。",
    options: Object.entries(QUOTATION_STATUS_LABEL).map(([key, label]) => ({
      key,
      label,
    })),
    value: (q) => q.status,
    toInput: (v) => ({ status: (v ?? undefined) as QuotationStatus }),
  },
  requested_at: {
    label: "依頼受領日",
    kind: "date",
    value: (q) => q.requested_at,
    toInput: (v) => ({ requested_at: v }),
  },
  quoted_date: {
    label: "見積日",
    kind: "date",
    value: (q) => q.quoted_date,
    toInput: (v) => ({ quoted_date: v }),
  },
  valid_until: {
    label: "有効期限",
    kind: "date",
    description: "提出した見積の有効期限です。客先への回答期限とは別物です。",
    value: (q) => q.valid_until,
    toInput: (v) => ({ valid_until: v }),
  },
  answer_due_date: {
    label: "回答期限",
    kind: "date",
    description: "客先へ回答する期限です。見積の有効期限とは別物です。",
    value: (q) => q.answer_due_date,
    toInput: (v) => ({ answer_due_date: v }),
  },
  client_contact_name: {
    label: "客先担当",
    value: (q) => q.client_contact_name,
    toInput: (v) => ({ client_contact_name: v }),
  },
  ship_to: {
    label: "納入先",
    value: (q) => q.ship_to,
    toInput: (v) => ({ ship_to: v }),
  },
  note: {
    label: "備考",
    kind: "textarea",
    description: "見積書に載ります。社内だけのメモは詳細の「社内備考」へ。",
    value: (q) => q.note,
    toInput: (v) => ({ note: v }),
  },
} satisfies Record<string, QuotationField>;

export interface QuotationColumnHandlers {
  canWrite: boolean;
  /** まとめて削除する対象として選ばれている見積の id。 */
  selectedIds: Set<number>;
  onToggleSelect: (quotation: Quotation) => void;
  onEditField: (quotation: Quotation, field: QuotationField) => void;
  /** セット品の印を切り替える（スイッチなのでダイアログを通さない）。 */
  onToggleSetProduct: (quotation: Quotation) => void;
}

/**
 * 一覧の列を組み立てる。
 *
 * 選択列（まとめて削除するためのチェックボックス）は**書き込める人にだけ**
 * 出す。選ぶ目的が削除なので、押せない人には場所を取らせない。全行の選択は
 * 表の上の「すべて選択」で行う（ヘッダのセルは並び替えのボタンで埋まっており、
 * 中に押せるものを置けない）。
 */
export function buildQuotationColumns({
  canWrite,
  selectedIds,
  onToggleSelect,
  onEditField,
  onToggleSetProduct,
}: QuotationColumnHandlers): ColumnDef<Quotation, never>[] {
  /**
   * その行を直せるか。**受注済みの見積は直せない**。
   *
   * 受注すると明細は案件へ写され、あとから見積を直しても案件は追従しない。
   * 直すときは受注を取り消してからにする（詳細画面と同じ決まり。
   * [[QuotationDetail]]）。鉛筆が消えるので、一覧でも押せないことが分かる。
   */
  const rowEditable = (quotation: Quotation) =>
    canWrite && quotation.status !== "ordered";

  /** 直せる項目のセル。見せる文字列は列ごとに渡す（値と表示が違うことがある）。 */
  const editable =
    (field: QuotationField, tone?: "danger" | "danger-strong") =>
    (quotation: Quotation, display: string | null) => (
      <EditableTextCell
        label={field.label}
        value={display}
        canWrite={rowEditable(quotation)}
        tone={tone}
        onEdit={() => onEditField(quotation, field)}
      />
    );

  /** チップで見せている項目を押して直せるようにする（[[EditableChip]]）。 */
  const editableChip =
    (field: QuotationField) => (quotation: Quotation, chip: React.ReactNode) => (
      <EditableChip
        label={`${field.label}を編集`}
        canWrite={rowEditable(quotation)}
        onPress={() => onEditField(quotation, field)}
      >
        {chip}
      </EditableChip>
    );

  const dataColumns = [
    helper.accessor("quotation_number", {
      id: "quotation_number",
      header: "見積番号",
      size: 140,
      meta: {
        label: "見積番号",
        mandatory: true,
        // 横スクロールしても「どの見積の行か」が分かるように左へ固定する。
        pinned: true,
        sortKey: "quotation_number",
      },
      // 受注済みは番号の右に緑のチェックを添える。状態列は既定で見えていても
      // 横に離れているので、固定されている番号の隣で決着済みが分かるようにする。
      cell: (ctx) => (
        <div className="flex items-center gap-1">
          <LinkCell
            to={`/quotations/${ctx.row.original.quotation_id}`}
            label={`見積 ${ctx.getValue()} を開く`}
            bold
          >
            {ctx.getValue()}
          </LinkCell>
          {ctx.row.original.status === "ordered" && (
            <CircleCheck
              size={16}
              role="img"
              aria-label="受注済"
              className="shrink-0 text-success"
            />
          )}
        </div>
      ),
    }),
    helper.accessor("completed_item_count", {
      id: "progress",
      header: "進捗",
      size: 100,
      meta: { label: "進捗" },
      // **見積番号のすぐ右**に置く。一覧でまず知りたいのは「どの見積が残っているか」
      // で、番号と並べておくと目を左右に動かさずに拾える。
      cell: (ctx) => {
        const quotation = ctx.row.original;
        return (
          <ProgressGauge
            value={quotation.completed_item_count}
            total={quotation.item_count}
            label={`明細 ${quotation.item_count} 件中 ${quotation.completed_item_count} 件が見積完了`}
          />
        );
      },
    }),
    helper.accessor("client_name", {
      id: "client_name",
      header: "得意先",
      size: 200,
      meta: { label: "得意先" },
      // **一覧からは変えさせない**。得意先を替えると回答ファイルの様式（指定業者）
      // まで変わるので、流し見の最中に押し替えられると困る。変えるのは見積詳細の
      // 「見積依頼情報」から。
      cell: (ctx) => ctx.getValue() ?? "—",
    }),
    helper.accessor("client_request_number", {
      id: "client_request_number",
      header: "客先依頼番号",
      size: 150,
      meta: { label: "客先依頼番号", sortKey: "client_request_number" },
      cell: (ctx) =>
        editable(FIELDS.client_request_number)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("is_set_product", {
      id: "is_set_product",
      header: "セット品",
      size: 100,
      meta: { label: "セット品" },
      // セット品かどうかは**明細の数量の読み方が変わる**（畳んだ行の数量は購番を
      // またいだ合算）。案件一覧と同じスイッチで切り替える。
      //
      // **押したときの行き先は 2 つある**（判断は一覧側）。
      //
      // - 購番の行が付いている見積 … 明細の形も変わるので確認ダイアログを通す
      //   （[[set_product_fold]]）。**受注済みでも押せる**。案件の明細も同時に
      //   作り直すので、見積だけが先に進んでしまうことがない
      // - 紙の行が無い見積 … 印とセット数だけが動くのでその場で切り替える。
      //   **外すとセット数も消える**ので、消えたことは一覧側が知らせる
      cell: (ctx) => {
        const quotation = ctx.row.original;
        const folds = quotation.client_order_numbers.length > 0;
        return (
          <Switch
            size="sm"
            isSelected={quotation.is_set_product}
            // 畳み込みの側は受注済みでも押せる（受注で止めるのは見積の中身で、
            // ここは案件と一緒に作り直す操作だから）。
            isDisabled={folds ? !canWrite : !rowEditable(quotation)}
            onValueChange={() => onToggleSetProduct(quotation)}
            aria-label={`見積 ${quotation.quotation_number} のセット品`}
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        );
      },
    }),
    helper.accessor("set_count", {
      id: "set_count",
      header: "セット数",
      size: 90,
      meta: { label: "セット数", numeric: true },
      // 案件一覧と同じ決まり。購番の行が付いている見積のセット数は**購番の本数
      // から決まる導出値**なので直せない（API も 409）。紙の行の無い見積だけ
      // 手で入れられる。
      cell: (ctx) => {
        const quotation = ctx.row.original;
        const display =
          quotation.set_count == null ? null : `${quotation.set_count} セット`;
        if (quotation.client_order_numbers.length > 0) {
          return display == null ? (
            <span className="text-default-400">—</span>
          ) : (
            <span>{display}</span>
          );
        }
        return editable(FIELDS.set_count)(quotation, display);
      },
    }),
    helper.accessor("client_order_numbers", {
      id: "client_order_numbers",
      header: "客先購番",
      size: 220,
      meta: { label: "客先購番" },
      // 畳む前の購番（[[client_order_lines]]）。**直せない**。紙の行そのものの
      // 記録なので画面から書き換えさせない。客先依頼番号は 1 本しか持てないので、
      // セット品の購番はこの列に並ぶ。
      cell: (ctx) => {
        const numbers = ctx.row.original.client_order_numbers;
        if (numbers.length === 0) return <span className="text-default-400">—</span>;
        return <span className="tabular-nums">{numbers.join(" / ")}</span>;
      },
    }),
    helper.accessor("subject", {
      id: "subject",
      header: "件名",
      size: 200,
      meta: { label: "件名" },
      cell: (ctx) => editable(FIELDS.subject)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("is_urgent", {
      id: "is_urgent",
      header: "特急",
      size: 80,
      meta: { label: "特急" },
      // 特急でない行は空にする。並べたときに特急だけが目に入るようにするため。
      cell: (ctx) =>
        editableChip(FIELDS.is_urgent)(
          ctx.row.original,
          ctx.getValue() ? (
            <Chip size="sm" variant="flat" color="danger">
              特急
            </Chip>
          ) : (
            <span className="text-default-300">—</span>
          ),
        ),
    }),
    helper.accessor("status", {
      id: "status",
      header: "状態",
      size: 110,
      meta: { label: "状態", sortKey: "status" },
      cell: (ctx) =>
        editableChip(FIELDS.status)(
          ctx.row.original,
          <Chip size="sm" variant="flat" color={STATUS_COLOR[ctx.getValue()]}>
            {QUOTATION_STATUS_LABEL[ctx.getValue()]}
          </Chip>,
        ),
    }),
    helper.accessor("requested_at", {
      id: "requested_at",
      header: "依頼受領日",
      size: 120,
      meta: { label: "依頼受領日", sortKey: "requested_at" },
      cell: (ctx) => editable(FIELDS.requested_at)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("quoted_date", {
      id: "quoted_date",
      header: "見積日",
      size: 120,
      meta: { label: "見積日", sortKey: "quoted_date" },
      cell: (ctx) => editable(FIELDS.quoted_date)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("valid_until", {
      id: "valid_until",
      header: "有効期限",
      size: 120,
      meta: { label: "有効期限", sortKey: "valid_until" },
      cell: (ctx) => {
        const quotation = ctx.row.original;
        // 期限切れは赤で示す（決着済み＝受注・失注・辞退は期限の意味が無いので
        // 通常表示）。編集できるようにしても色の意味が消えないようにする。
        const expired =
          quotation.valid_until != null &&
          quotation.valid_until < today() &&
          !settled(quotation.status);
        return editable(FIELDS.valid_until, expired ? "danger" : undefined)(
          quotation,
          quotation.valid_until,
        );
      },
    }),
    helper.accessor("answer_due_date", {
      id: "answer_due_date",
      header: "回答期限",
      size: 120,
      meta: { label: "回答期限", sortKey: "answer_due_date" },
      cell: (ctx) => {
        const quotation = ctx.row.original;
        // 回答前に期限を過ぎている行は**太字の赤**で示す（有効期限より急ぐので
        // 強く出す）。決着済みは期限の意味が無いので通常表示。
        const overdue =
          quotation.answer_due_date != null &&
          quotation.answer_due_date < today() &&
          !settled(quotation.status);
        return editable(FIELDS.answer_due_date, overdue ? "danger-strong" : undefined)(
          quotation,
          quotation.answer_due_date,
        );
      },
    }),
    helper.accessor("answered_deadline", {
      id: "answered_deadline",
      header: "回答納期",
      size: 120,
      meta: { label: "回答納期" },
      // **明細から導く値**（見積ヘッダは持たない）ので直せない。明細の回答納期の
      // うち最も遅い日で、見積 1 件がいつ納まるかを一覧から読めるようにする。
      // 行の回答納期が空なら客先の希望どおりの回答なので、希望納期で数えている。
      cell: (ctx) => ctx.getValue() ?? <span className="text-default-300">—</span>,
    }),
    helper.accessor("client_contact_name", {
      id: "client_contact_name",
      header: "客先担当",
      size: 130,
      meta: { label: "客先の依頼担当者" },
      cell: (ctx) =>
        editable(FIELDS.client_contact_name)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("ship_to", {
      id: "ship_to",
      header: "納入先",
      size: 150,
      meta: { label: "納入先" },
      cell: (ctx) => editable(FIELDS.ship_to)(ctx.row.original, ctx.getValue()),
    }),
    helper.accessor("item_count", {
      id: "item_count",
      header: "明細",
      size: 80,
      meta: { label: "明細行数", numeric: true },
      cell: (ctx) => ctx.getValue(),
    }),
    helper.accessor("total_amount", {
      id: "total_amount",
      header: "金額",
      size: 130,
      meta: { label: "見積金額", numeric: true },
      cell: (ctx) => formatAmount(ctx.getValue()),
    }),
    helper.accessor("note", {
      id: "note",
      header: "備考",
      size: 200,
      meta: { label: "備考" },
      cell: (ctx) => editable(FIELDS.note)(ctx.row.original, ctx.getValue()),
    }),
  ];

  if (!canWrite) return dataColumns as ColumnDef<Quotation, never>[];
  return [
    helper.display({
      id: "select",
      header: "選択",
      size: 60,
      enableResizing: false,
      meta: { label: "選択", mandatory: true, pinned: true },
      cell: (ctx) => {
        const quotation = ctx.row.original;
        return (
          <Checkbox
            size="sm"
            isSelected={selectedIds.has(quotation.quotation_id)}
            onValueChange={() => onToggleSelect(quotation)}
            aria-label={`見積 ${quotation.quotation_number} を選択`}
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        );
      },
    }),
    ...dataColumns,
  ] as ColumnDef<Quotation, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。 */
export const quotationColumnIds = [
  "select",
  "quotation_number",
  "progress",
  "client_name",
  "client_request_number",
  "is_set_product",
  "set_count",
  "client_order_numbers",
  "subject",
  "is_urgent",
  "status",
  "requested_at",
  "quoted_date",
  "valid_until",
  "answer_due_date",
  "answered_deadline",
  "client_contact_name",
  "ship_to",
  "item_count",
  "total_amount",
  "note",
];

/**
 * 既定では非表示にする列。
 *
 * 客先の依頼に由来する項目は、見積を作るときには要るが一覧を流し見するときには
 * 邪魔になる。列が増えて横に伸びすぎないよう、使う人が表示列の設定で出す。
 */
export const quotationDefaultHiddenColumnIds = [
  "subject",
  "client_contact_name",
  "ship_to",
  // 購番の列は案件一覧と同じく既定で畳む。セット品の見積でしか値が入らず、
  // 1 本しか無い見積では客先依頼番号と同じ値が並ぶだけになるため。
  "client_order_numbers",
];

/** 左に固定する列の id。並べ替えても先頭に留める。 */
export const quotationPinnedColumnIds = ["select", "quotation_number"];
